import { useState, useEffect, useMemo, useRef } from "react";
import { FiX, FiPlus, FiTrash2 } from "react-icons/fi";

const OtherChargesDrawer = ({
  isOpen = false,
  onClose = () => {},
  onSave = () => {},
  initialCharges = [],
  isFullWidth = false,
  drawerWidth = null,
}) => {
  const [charges, setCharges] = useState([]);
  const [errors, setErrors] = useState({});
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);

  // FIX: keep a ref to the latest initialCharges so the seed effect doesn't
  // need it as a dependency (avoids resetting user input on parent re-renders).
  const initialChargesRef = useRef(initialCharges);
  useEffect(() => {
    initialChargesRef.current = initialCharges;
  }, [initialCharges]);

  // Simple scroll prevention
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // FIX: only depend on isOpen. Read initialCharges from the ref so an inline
  // `[]` literal (or any parent re-render) can't clobber the user's typing.
  // FIX: deep-clone initialCharges and normalize `amount` to a string so the
  // controlled number input behaves consistently.
  useEffect(() => {
    if (isOpen) {
      const incoming = initialChargesRef.current || [];
      setCharges(
        incoming.length > 0
          ? incoming.map((c) => ({
              ...c,
              description: c.description ?? "",
              amount:
                c.amount === null || c.amount === undefined
                  ? ""
                  : String(c.amount),
            }))
          : [{ description: "", amount: "" }]
      );
      setErrors({});
      setHasAttemptedSave(false);
    }
  }, [isOpen]);

  // Validate all charges
  const validateCharges = (chargesList) => {
    const newErrors = {};
    chargesList.forEach((charge, index) => {
      const chargeErrors = {};
      if (!charge.description || !charge.description.trim()) {
        chargeErrors.description = "Description is required";
      }
      if (
        charge.amount === "" ||
        charge.amount === null ||
        charge.amount === undefined ||
        isNaN(parseFloat(charge.amount)) ||
        parseFloat(charge.amount) <= 0
      ) {
        chargeErrors.amount = "Valid amount greater than 0 is required";
      }
      if (Object.keys(chargeErrors).length > 0) {
        newErrors[index] = chargeErrors;
      }
    });
    return newErrors;
  };

  // Re-validate on changes if user has already attempted save
  useEffect(() => {
    if (hasAttemptedSave) {
      setErrors(validateCharges(charges));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [charges, hasAttemptedSave]);

  const handleAddCharge = () => {
    setCharges([...charges, { description: "", amount: "" }]);
  };

  // FIX: no manual error-shifting. The re-validate effect above will recompute
  // errors against the new list with correct keys. This keeps a single source
  // of truth for error state.
  const handleRemoveCharge = (index) => {
    if (charges.length > 1) {
      setCharges(charges.filter((_, i) => i !== index));
    }
  };

  // FIX: do not mutate objects inside state. Produce new objects instead so
  // React reliably re-renders and the re-validate effect actually fires.
  const handleChargeChange = (index, field, value) => {
    setCharges((prev) =>
      prev.map((charge, i) =>
        i === index ? { ...charge, [field]: value } : charge
      )
    );

    // Clear the specific field error as user types (visual feedback only;
    // the re-validate effect will overwrite this with authoritative state
    // on the next pass if the field is still invalid).
    setErrors((prev) => {
      if (!prev[index]?.[field]) return prev;
      const updated = { ...prev };
      updated[index] = { ...updated[index] };
      delete updated[index][field];
      if (Object.keys(updated[index]).length === 0) {
        delete updated[index];
      }
      return updated;
    });
  };

  const handleSave = () => {
    setHasAttemptedSave(true);
    const validationErrors = validateCharges(charges);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      return; // Block save if any validation errors
    }

    // Filter out empty charges (safety net)
    const validCharges = charges
      .filter((charge) => charge.description.trim() && charge.amount)
      .map((charge) => ({
        ...charge,
        amount: parseFloat(charge.amount) || 0,
      }));

    onSave(validCharges);
    onClose();
  };

  // FIX: memoize so we don't recompute on unrelated re-renders (minor).
  const total = useMemo(
    () =>
      charges.reduce((sum, charge) => {
        const amount = parseFloat(charge.amount) || 0;
        return sum + amount;
      }, 0),
    [charges]
  );

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-end bg-black bg-opacity-40"
      // FIX: backdrop click closes the drawer, matching the rest of the app.
      onClick={onClose}
    >
      <div
        className={`relative w-full h-full bg-white shadow-xl ${
          !isFullWidth ? drawerWidth || "lg:w-2/5" : ""
        } `}
        // FIX: stop propagation so clicks inside the panel don't bubble to
        // the backdrop and close the drawer.
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
              <FiPlus className="w-4 h-4 text-white" />
            </div>
            <h2 className="text-xl font-semibold text-gray-800">
              Other Charges
            </h2>
          </div>
          <button
            onClick={onClose}
            // FIX: aria-label for accessibility and stable test queries.
            aria-label="Close"
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <FiX className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto h-[calc(100vh-200px)]">
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-4">
              Add additional charges such as late fees, damage deposits, special
              requests, etc.
            </p>

            <div className="space-y-4">
              {charges.map((charge, index) => (
                <div
                  key={index}
                  className={`p-4 border rounded-lg bg-gray-50 ${
                    errors[index] ? "border-red-300" : "border-gray-200"
                  }`}
                >
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-sm font-medium text-gray-700">
                      Charge #{index + 1}
                    </span>
                    {charges.length > 1 && (
                      <button
                        onClick={() => handleRemoveCharge(index)}
                        // FIX: aria-label so tests and screen readers can find it.
                        aria-label={`Remove charge ${index + 1}`}
                        className="p-1 hover:bg-red-50 rounded text-red-500 hover:text-red-700"
                      >
                        <FiTrash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={charge.description}
                        onChange={(e) =>
                          handleChargeChange(
                            index,
                            "description",
                            e.target.value
                          )
                        }
                        placeholder="E.g., Late pickup fee, Damage deposit, Special handling, etc."
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:border-transparent ${
                          errors[index]?.description
                            ? "border-red-400 focus:ring-red-500"
                            : "border-gray-300 focus:ring-blue-500"
                        }`}
                      />
                      {errors[index]?.description && (
                        <p className="mt-1 text-xs text-red-500">
                          {errors[index].description}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Amount (₹) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        value={charge.amount}
                        onChange={(e) =>
                          handleChargeChange(index, "amount", e.target.value)
                        }
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:border-transparent ${
                          errors[index]?.amount
                            ? "border-red-400 focus:ring-red-500"
                            : "border-gray-300 focus:ring-blue-500"
                        }`}
                      />
                      {errors[index]?.amount && (
                        <p className="mt-1 text-xs text-red-500">
                          {errors[index].amount}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={handleAddCharge}
              className="mt-4 w-full py-2 px-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-gray-400 hover:text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
            >
              <FiPlus className="w-4 h-4" />
              Add Another Charge
            </button>
          </div>

          {/* Validation Summary */}
          {hasAttemptedSave && Object.keys(errors).length > 0 && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700 font-medium">
                Please fill in all required fields before saving.
              </p>
            </div>
          )}

          {/* Total Preview */}
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-700">
                Total Other Charges:
              </span>
              <span className="text-lg font-bold text-blue-700">
                ₹{total.toFixed(2)}
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-6 border-t bg-white">
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 py-3 px-4 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={hasAttemptedSave && Object.keys(errors).length > 0}
              className={`flex-1 py-3 px-4 font-semibold rounded-lg transition-colors ${
                hasAttemptedSave && Object.keys(errors).length > 0
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700"
              }`}
            >
              Save Charges
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OtherChargesDrawer;
